import * as yup from 'yup';
import { useFormik } from 'formik';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import {
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  useEventCallback,
  IconButton,
} from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiButton } from 'shell/components/shared/button';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { ValueFeatureAuth, BodyValueFeatureAuth } from './types';
import { OrderTypes } from 'shell/constants/order-types';
import { noop } from 'shell/utils/noop';
import { AddCircleOutline } from '@mui/icons-material';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { regexOnlyNumber } from 'shell/constants/regex';
import Delete from '@mui/icons-material/Delete';
import AuthorizationValue, { AuthorizationValueFormVM } from './authorization-value-modal';

const orderDefault = { order: OrderTypes.Asc };

export interface FeatureAuthForm {
  code?: string;
  description?: string;
  contextLastUpdate?: string;
  valuesFeatureAuth?: ValueFeatureAuth;
}

interface FormProps {
  onOk: (FormComponent?: FeatureAuthForm) => void;
  initialValues?: FeatureAuthForm;
  onCancel: () => void;
  disabled?: boolean;
  title?: string;
  isEdit?: boolean;
  redirectPath?: string;
}
const validationSchema = yup.object({
  code: yup
    .string()
    .nullable()
    .required('Il campo è obbligatorio')
    .matches(regexOnlyNumber, 'Il campo è numerico'),
  description: yup.string().nullable().required('Il campo è obbligatorio'),
});

const FormComponent = ({
  onOk,
  initialValues,
  onCancel,
  disabled = false,
  title,
  isEdit = false,
}: FormProps): React.ReactElement => {
  const [viewValore, setViewValore] = useState<boolean>(false);

  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const featureAuthForm = useFormik({
    initialValues: initialValues || {
      code: '',
      description: '',
      contextLastUpdate: '',
      valuesFeatureAuth: {
        body: [],
        page: 0,
        rowsPerPage: 10,
        orders: orderDefault,
      },
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const onTrashPress = useEventCallback(
    (cell: BodyValueFeatureAuth): Promise<BodyValueFeatureAuth> => {
      return new Promise((resolve) => {
        let newBody = featureAuthForm.values?.valuesFeatureAuth?.body?.slice() || [];

        newBody = newBody.filter((item: BodyValueFeatureAuth) => item.value !== cell.value);

        const newValuesFeatureAuth: ValueFeatureAuth = {
          page: featureAuthForm.values?.valuesFeatureAuth?.page || 0,
          rowsPerPage: featureAuthForm.values?.valuesFeatureAuth?.rowsPerPage || 10,
          orders: featureAuthForm.values?.valuesFeatureAuth?.orders || orderDefault,
          body: newBody,
        };

        featureAuthForm.setFieldValue('valuesFeatureAuth', newValuesFeatureAuth);
        resolve(cell);
      });
    }
  );

  const handleChangePage = useCallback(
    (newPage) => {
      const newValuesFeatureAuth: ValueFeatureAuth = {
        page: newPage || 0,
        rowsPerPage: featureAuthForm.values?.valuesFeatureAuth?.rowsPerPage || 10,
        orders: featureAuthForm.values?.valuesFeatureAuth?.orders || orderDefault,
        body: featureAuthForm.values?.valuesFeatureAuth?.body || [],
      };
      featureAuthForm.setFieldValue('valuesFeatureAuth', newValuesFeatureAuth);
    },
    [featureAuthForm.setFieldValue, featureAuthForm.values?.valuesFeatureAuth]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newValuesFeatureAuth: ValueFeatureAuth = {
        page: 0,
        rowsPerPage: rowsPerPage || 10,
        orders: featureAuthForm.values?.valuesFeatureAuth?.orders || orderDefault,
        body: featureAuthForm.values?.valuesFeatureAuth?.body || [],
      };

      featureAuthForm.setFieldValue('valuesFeatureAuth', newValuesFeatureAuth);
    },
    [featureAuthForm.setFieldValue, featureAuthForm.values?.valuesFeatureAuth]
  );

  const onAddValore = useEventCallback((values: AuthorizationValueFormVM) => {
    const newBody: Array<BodyValueFeatureAuth> =
      featureAuthForm.values.valuesFeatureAuth?.body || [];

    const alredyPresent = newBody.find((item) => item.value === values?.value);

    if (alredyPresent) {
      // close
      setViewValore(false);

      return;
    }

    const firstElement: Array<BodyValueFeatureAuth> = [
      {
        value: values?.value || '',
        description: values?.description || '',
      },
    ];

    featureAuthForm.setFieldValue('valuesFeatureAuth', {
      ...featureAuthForm.values.valuesFeatureAuth,
      body: firstElement.concat(newBody),
    });

    // close
    setViewValore(false);
  });

  const body: Array<BodyValueFeatureAuth> | undefined = useMemo(() => {
    if (!featureAuthForm.values.valuesFeatureAuth?.body?.length) {
      return undefined;
    }

    return featureAuthForm.values.valuesFeatureAuth?.body.map((item) => ({
      ...item,
      action: (
        <IconButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onTrashPress(item);
          }}
          aria-label="Elimina elemento"
          aria-haspopup="true"
          color="cdmBrand"
          disabled={!isEdit}
          style={{
            padding: '4px',
          }}
        >
          <Delete />
        </IconButton>
      ),
    }));
  }, [featureAuthForm.values.valuesFeatureAuth?.body]);

  return (
    <Fragment>
      <Card raised className="pmi-my-3">
        <form onSubmit={featureAuthForm.handleSubmit}>
          <CardContent style={{ padding: '24px' }}>
            <Typography gutterBottom variant="h5" component="h2" color="secondary">
              {title}
            </Typography>
            <div style={{ padding: '24px 0 0 0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <PmiTextField
                    error={featureAuthForm.touched.code && Boolean(featureAuthForm.errors.code)}
                    helperText={featureAuthForm.errors.code}
                    aria-label="Codice"
                    label="Codice *"
                    id="code"
                    name="code"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={featureAuthForm.values.code}
                    disabled={disabled || isEdit}
                    onChange={featureAuthForm.handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <PmiTextField
                    error={
                      featureAuthForm.touched.description &&
                      Boolean(featureAuthForm.errors.description)
                    }
                    helperText={featureAuthForm.errors.description}
                    aria-label="Descrizione"
                    label="Descrizione *"
                    id="description"
                    name="description"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={featureAuthForm.values.description}
                    disabled={disabled}
                    onChange={featureAuthForm.handleChange}
                    inputProps={{
                      maxLength: '50',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <PmiTextField
                    aria-label="Contesto ultimo aggiornamento"
                    label="Contesto ultimo aggiornamento"
                    id="contextLastUpdate"
                    name="contextLastUpdate"
                    fullWidth
                    type="text"
                    variant="outlined"
                    value={featureAuthForm.values.contextLastUpdate}
                    disabled={disabled}
                    onChange={featureAuthForm.handleChange}
                    inputProps={{
                      maxLength: '50',
                    }}
                  />
                </Grid>
                <Grid container spacing={2} item xs={12}>
                  <Grid item xs={12} md={9}>
                    <PmiTitleDivider>Valori Autorizzazione</PmiTitleDivider>
                  </Grid>
                  {disabled ? undefined : (
                    <Grid item xs={12} md={3}>
                      <IconButton
                        onClick={() => setViewValore(true)}
                        aria-label="Aggiungi nuovo valore"
                        aria-haspopup="true"
                      >
                        <AddCircleOutline />
                      </IconButton>
                      Aggiungi nuovo valore
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {featureAuthForm.values.valuesFeatureAuth?.body?.length ? (
                <Grid item xs={12}>
                  <Table
                    onSort={noop}
                    noSort
                    head={headCells}
                    orders={featureAuthForm.values.valuesFeatureAuth?.orders || orderDefault}
                    page={featureAuthForm.values.valuesFeatureAuth?.page || 0}
                    rowsPerPage={featureAuthForm.values.valuesFeatureAuth?.rowsPerPage || 10}
                    body={body || []}
                    count={featureAuthForm.values.valuesFeatureAuth?.body?.length || 0}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    minWidth={800}
                    ariaLabel="Lista Valori Autorizzazione"
                    hideButtons
                  />
                </Grid>
              ) : undefined}
            </div>
          </CardContent>
          <CardActions style={{ padding: '24px 24px 16px 24px' }}>
            <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
              Conferma
            </PmiButton>
            <PmiButton
              variant="outlined"
              color="cdmBrand"
              onClick={() => {
                onCancel();
              }}
            >
              Indietro
            </PmiButton>
          </CardActions>
        </form>
      </Card>
      {viewValore ? (
        <AuthorizationValue
          isOpen={viewValore}
          onCancel={() => setViewValore(false)}
          onOk={onAddValore}
        />
      ) : undefined}
    </Fragment>
  );
};
FormComponent.displayName = 'feature auth form component';

const Form = memo(FormComponent);

export default Form;
